'use client';

import React, { useState } from 'react';
import Button from '@/components/ui/Button';
import Icon from '@/components/ui/AppIcon';

interface Question {
    id: number;
    text: string;
    options: {
        text: string;
        weights: { dog: number; cat: number; reptile: number; bird: number };
    }[];
}

const questions: Question[] = [
    {
        id: 1,
        text: "How would you describe your daily activity level?",
        options: [
            { text: "Very Active - I love outdoor exercise", weights: { dog: 3, cat: 0, reptile: 0, bird: 0 } },
            { text: "Moderate - I enjoy a daily walk", weights: { dog: 2, cat: 1, reptile: 0, bird: 0 } },
            { text: "Low - I prefer relaxing at home", weights: { dog: 0, cat: 3, reptile: 3, bird: 2 } }
        ]
    },
    {
        id: 2,
        text: "How much time can you dedicate to pet care each day?",
        options: [
            { text: "More than 2 hours", weights: { dog: 3, cat: 1, reptile: 1, bird: 3 } },
            { text: "1-2 hours", weights: { dog: 2, cat: 2, reptile: 2, bird: 2 } },
            { text: "Less than 1 hour", weights: { dog: 0, cat: 3, reptile: 3, bird: 1 } }
        ]
    },
    {
        id: 3,
        text: "What is your living situation?",
        options: [
            { text: "House with a yard", weights: { dog: 3, cat: 2, reptile: 2, bird: 2 } },
            { text: "Apartment / Condo", weights: { dog: 1, cat: 3, reptile: 3, bird: 3 } },
            { text: "Shared living space", weights: { dog: 0, cat: 2, reptile: 3, bird: 2 } }
        ]
    },
    {
        id: 4,
        text: "What kind of companionship are you looking for?",
        options: [
            { text: "A constant companion and playmate", weights: { dog: 3, cat: 1, reptile: 0, bird: 2 } },
            { text: "Independent but affectionate", weights: { dog: 0, cat: 3, reptile: 1, bird: 0 } },
            { text: "Quiet and low-maintenance", weights: { dog: 0, cat: 1, reptile: 3, bird: 0 } },
            { text: "Interesting to observe and interact with", weights: { dog: 0, cat: 0, reptile: 3, bird: 3 } }
        ]
    },
    {
        id: 5,
        text: "How do you feel about pet grooming and cleaning?",
        options: [
            { text: "I don't mind it at all", weights: { dog: 3, cat: 1, reptile: 1, bird: 1 } },
            { text: "I prefer minimal maintenance", weights: { dog: 0, cat: 3, reptile: 2, bird: 2 } },
            { text: "I want an easy-to-clean environment", weights: { dog: 0, cat: 2, reptile: 3, bird: 1 } }
        ]
    }
];

const CompatibilityQuiz = () => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [scores, setScores] = useState({ dog: 0, cat: 0, reptile: 0, bird: 0 });
    const [showResult, setShowResult] = useState(false);

    const handleOptionClick = (weights: { dog: number; cat: number; reptile: number; bird: number }) => {
        const newScores = {
            dog: scores.dog + weights.dog,
            cat: scores.cat + weights.cat,
            reptile: scores.reptile + weights.reptile,
            bird: scores.bird + weights.bird
        };

        setScores(newScores);

        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            setShowResult(true);
        }
    };

    const resetQuiz = () => {
        setScores({ dog: 0, cat: 0, reptile: 0, bird: 0 });
        setCurrentQuestionIndex(0);
        setShowResult(false);
    };

    const getResult = () => {
        const maxScore = Math.max(...Object.values(scores));
        const resultType = Object.keys(scores).find(key => scores[key as keyof typeof scores] === maxScore);

        switch (resultType) {
            case 'dog':
                return {
                    title: 'A Loyal Dog',
                    description: "Your active lifestyle and desire for companionship make a dog the perfect match! Dogs require time and energy but offer unconditional love and loyalty in return.",
                    icon: '🐕',
                    link: '/pet-discovery?category=dog'
                };
            case 'cat':
                return {
                    title: 'A Friendly Cat',
                    description: "A cat seems like your ideal companion! They offer affection on their own terms and fit well into various living situations, making them great for busy yet loving owners.",
                    icon: '🐈',
                    link: '/pet-discovery?category=cat'
                };
            case 'reptile':
                return {
                    title: 'A FASCINATING Reptile',
                    description: "You appreciate unique and low-maintenance pets. A reptile (like a gecko or bearded dragon) fits your preference for a quiet, interesting observing companion.",
                    icon: '🦎',
                    link: '/pet-discovery?category=reptile'
                };
            case 'bird':
                return {
                    title: 'A Cheerful Bird',
                    description: "Your answers suggest you might enjoy the intelligence and personality of a bird. From parakeets to parrots, they can be wonderful, interactive friends.",
                    icon: '🦜',
                    link: '/pet-discovery?category=bird'
                };
            default:
                return {
                    title: 'A Cuddly Cat',
                    description: "Based on your balanced answers, a cat might be the safest bet for a loving, flexible companion!",
                    icon: '🐈',
                    link: '/pet-discovery?category=cat'
                };
        }
    };

    if (showResult) {
        const result = getResult();
        return (
            <div className="text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-5xl">
                    {result.icon}
                </div>
                <h3 className="text-2xl font-headline font-bold text-foreground">
                    You matched with: {result.title}
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                    {result.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                    <Button onClick={resetQuiz} variant="outline">
                        Retake Quiz
                    </Button>
                    <Button href={result.link}>
                        View Available Pets
                    </Button>
                </div>
            </div>
        );
    }

    const question = questions[currentQuestionIndex];

    return (
        <div className="max-w-xl mx-auto">
            <div className="mb-8">
                <div className="flex justify-between text-sm text-muted-foreground mb-2">
                    <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
                    <span>{Math.round(((currentQuestionIndex) / questions.length) * 100)}% Complete</span>
                </div>
                <div className="w-full bg-secondary/30 h-2 rounded-full overflow-hidden">
                    <div
                        className="bg-primary h-full transition-all duration-300 ease-out"
                        style={{ width: `${((currentQuestionIndex) / questions.length) * 100}%` }}
                    />
                </div>
            </div>

            <h3 className="text-xl font-headline font-semibold text-foreground mb-6 text-center">
                {question.text}
            </h3>

            <div className="grid gap-3">
                {question.options.map((option, index) => (
                    <button
                        key={index}
                        onClick={() => handleOptionClick(option.weights)}
                        className="p-4 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all text-left font-medium text-foreground"
                    >
                        {option.text}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default CompatibilityQuiz;
